import {
  type ResultOf,
  type VariablesOf,
} from '@graphql-typed-document-node/core'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMountedState } from 'react-use'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { exists } from '~/utils'

import { useTabulationQueryKey } from './useTabulationQueryKey'

const mutationDocument = graphql(`
  mutation StrategyMemoEditPage_TabulationMutation(
    $surveyId: String!
    $brandGroupId: Int!
    $filter: [[CustomerAttributeFilterItemInput!]!]!
  ) {
    count: countBrands9segsInBrandGroup(
      surveyId: $surveyId
      brandGroupId: $brandGroupId
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)
type MutationVariables = VariablesOf<typeof mutationDocument>

const queryDocument = graphql(`
  query StrategyMemoEditPage_Tabulation($awaitId: String!) {
    count: countBrands9segsInBrandGroup(awaitId: $awaitId) {
      arrived
      result {
        result {
          id
          number
          result {
            count
            segmentNumber
          }
        }
      }
    }
  }
`)
type QueryResult = NonNullable<
  ResultOf<typeof queryDocument>['count']
>['result']

export const useTabulationQuery = () => {
  const { t } = useTranslation()
  const graphQLRequest = useGraphQLRequest()
  const queryKey = useTabulationQueryKey()
  const [_, { brandGroupId, filter, surveyId }] = queryKey
  const isMounted = useMountedState()
  const enabled = React.useMemo(
    () => exists(surveyId) && exists(brandGroupId),
    [brandGroupId, surveyId],
  )

  return useQuery({
    queryFn: async () => {
      const mutationResult = await graphQLRequest({
        document: mutationDocument,
        variables: { surveyId, brandGroupId, filter } as MutationVariables,
      })
      const awaitId = mutationResult.count?.awaitId
      if (!exists(awaitId)) {
        throw new Error(t('failed_to_retrieve_awaitid_1'))
      }

      const result = await new Promise<QueryResult>((resolve) => {
        const refetchUntilArrived = async () => {
          if (!isMounted()) {
            resolve(null)

            return
          }

          const queryResult = await graphQLRequest({
            document: queryDocument,
            variables: { awaitId },
          })
          if (!queryResult.count?.arrived) {
            setTimeout(refetchUntilArrived, 1000)

            return
          }

          resolve(queryResult.count.result)
        }

        refetchUntilArrived()
      })

      if (!exists(result)) {
        throw new Error(t('failed_to_retrieve_result'))
      }

      return result.result ?? []
    },
    queryKey,
    enabled,
    gcTime: 0,
    retry: false,
  })
}

export type TabulationQueryResult = QueryResultOf<typeof useTabulationQuery>
